<script lang="ts" setup>
  // TODO: Добавить в form-kit и поправить типы
  import type { InputProps, SelectOption, SelectProps } from 'naive-ui'
  import type { RenderLabel } from 'naive-ui/es/_internal/select-menu/src/interface'
  import type { RenderTag } from 'naive-ui/es/_internal/selection/src/interface'
  import type { SelectBaseOption } from 'naive-ui/es/select/src/interface'
  import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'vue'

  import { type FormItemProps, getPropsKeys, IGNORE_PROPS, UiFormItem, useFormField } from '@uplatform-ai/fe-form-kit'
  import INTL, { type Country } from 'intl-tel-input'
  import utils from 'intl-tel-input/utils'
  import 'intl-tel-input/styles'

  import { getSearchOptions, useSearch } from '@/shared/lib/useSearch'

  interface Props extends FormItemProps<string | null> {
    /** DOM-свойства для элемента input внутри компонента */
    inputProps?: InputHTMLAttributes | TextareaHTMLAttributes
    /** Placeholder для ввода */
    placeholder?: string
    /** Указывает, нужно ли отключить input */
    disabled?: boolean
    /** Устанавливает состояние только для чтения */
    readonly?: boolean
    /** Callback, срабатывающий при потере фокуса */
    onBlur?: (event: FocusEvent) => void
    /** Callback, срабатывающий при установке фокуса */
    onFocus?: (event: FocusEvent) => void
    /** Callback, срабатывающий при вводе пользователем данных */
    onInput?: (value: string | [string, string]) => void
    /** Элемент для телепорта дропдауна (необходим в случае использования dialog) */
    dropdownTo?: string | boolean | HTMLElement | undefined
    /** Направление открытия дропдауна выбора страны */
    placement?: SelectProps['placement']
  }

  interface Emits {
    (e: 'update:value', data: string): void
    (e: 'update:iso', data: Country['iso2'] | undefined): void
  }

  const props = withDefaults(defineProps<Props>(), {
    inputProps: undefined,
    placeholder: '',
    disabled: undefined,
    readonly: undefined,
    onBlur: undefined,
    onFocus: undefined,
    dropdownTo: undefined,
    onInput: undefined,
    placement: undefined,
  })
  const emit = defineEmits<Emits>()

  // @ts-expect-error name type
  const { value, inputId, errorMessage, setValue } = useFormField(props)
  const { locale } = useI18n()

  const phoneIso = ref<Country['iso2']>()
  const inputRef = useTemplateRef('inputRef')
  const INTLCountries = computed(() => INTL.getAllCountries())

  const displayNamesByLocaleInstance = computed(() => new Intl.DisplayNames(locale.value, { type: 'region' }))

  const phoneCountriesOptions = computed<(SelectBaseOption<string, string> & { item: Country })[]>(() => {
    return INTLCountries.value.map((country) => {
      return {
        item: country,
        label: displayNamesByLocaleInstance.value.of(country.iso2.toUpperCase()) || '',
        value: country.iso2,
      }
    })
  })
  const searchOptions = getSearchOptions(['label'])
  const search = ref('')
  const { results: searchedPhoneCountriesOptions } = useSearch<SelectBaseOption<string, string>>(
    search,
    () => phoneCountriesOptions.value,
    searchOptions,
  )
  const parsedSearchedPhoneCountriesOptions = computed(() => {
    return searchedPhoneCountriesOptions.value.map((option) => option.item)
  })
  const selectedOption = computed(() => {
    return phoneCountriesOptions.value.find((option) => option.item.iso2 === phoneIso.value)
  })
  const inputProps = computed<InputProps>(() => {
    const result: Exclude<InputProps, keyof FormItemProps> = {
      inputProps: Object.assign({ id: inputId }, props.inputProps || {}),
    }

    getPropsKeys(props).forEach((key) => {
      if ([...IGNORE_PROPS, 'dropdownTo', 'placement'].filter((prop) => prop !== 'size').includes(key)) {
        return
      }

      if (props[key] !== undefined) {
        result[key] = props[key]
      }
    })

    if (errorMessage.value) {
      result.status = 'error'
    }

    return result
  })

  const renderTag: RenderTag = ({ option }) => {
    return h('div', { class: `iti__flag iti__${option.value}` })
  }
  const renderLabel: RenderLabel = (option: SelectOption) => {
    return h(
      'div',
      { class: 'flex flex-nowrap! items-center gap-2' },
      {
        default: () => [h('div', { class: `iti__flag iti__${option.value}` }), option.label],
      },
    )
  }

  /**
   * Определяет страну по E.164 номеру (например, "+79001234567").
   * Учитывает длину dialCode и areaCodes для разрешения коллизий (например, +1 для US/CA/...).
   */
  const findCountryByE164 = (e164: string): Country | undefined => {
    if (!e164 || !e164.startsWith('+')) {
      return undefined
    }
    const digits = e164.slice(1).replace(/\D/g, '')
    const countries = INTL.getAllCountries()

    // Все страны, чей dialCode является префиксом номера
    const matches = countries.filter((c) => digits.startsWith(c.dialCode))
    if (!matches.length) {
      return undefined
    }

    // Берём страны с самым длинным dialCode (наиболее специфичные)
    const maxLen = Math.max(...matches.map((c) => c.dialCode.length))
    const longest = matches.filter((c) => c.dialCode.length === maxLen)

    if (longest.length === 1) {
      return longest[0]
    }

    // Если несколько стран с одинаковым dialCode (например, +1) — проверяем areaCodes
    const rest = digits.slice(maxLen)
    const withAreaMatch = longest.find((c) => c.areaCodes?.some((area) => rest.startsWith(area)))
    if (withAreaMatch) {
      return withAreaMatch
    }

    // Иначе — главная страна (priority === 0), либо первая
    return longest.find((c) => c.priority === 0) ?? longest[0]
  }
  /**
   * Применяет E.164 номер: определяет страну и форматирует национальную часть.
   */
  const applyE164Phone = (e164: string) => {
    if (!e164) {
      phoneIso.value = undefined
      return
    }
    const country = findCountryByE164(e164)
    if (!country) {
      phoneIso.value = undefined
      setValue(e164)
      return
    }
    phoneIso.value = country.iso2
    const national = e164.replace('+', '').slice(country.dialCode.length)
    setValue(utils.formatNumberAsYouType(national, country.iso2))
  }
  const handleUpdatePhone = (e: string) => {
    if (!e && !value.value) {
      return
    }
    if (!e && !!value.value) {
      setValue(e)
      return
    }
    const tempAsType = utils.formatNumberAsYouType(e, selectedOption.value?.value)
    const error = utils.getValidationError(tempAsType, selectedOption.value?.value)

    if (error === 'INVALID_LENGTH' || error === 'TOO_LONG') {
      return
    }
    setValue(tempAsType)
    emit('update:value', tempAsType)
  }

  const focus = () => {
    inputRef.value?.focus()
  }

  const blur = () => {
    inputRef.value?.blur()
  }

  defineExpose({
    setValue,
    focus,
    blur,
    value: computed(() => value.value),
  })

  // При смене страны переформатируем уже введённый номер
  watch(phoneIso, (newIso, oldIso) => {
    if (newIso && newIso !== oldIso) {
      emit('update:iso', newIso)
    }
  })

  onMounted(() => {
    // @ts-expect-error value type
    applyE164Phone(value.value)
  })
</script>

<template>
  <UiFormItem>
    <NInputGroup>
      <NSelect
        v-model:value="phoneIso"
        class="max-w-14"
        :options="parsedSearchedPhoneCountriesOptions"
        :theme-overrides="{
          peers: {
            InternalSelection: {
              heightMedium: '40px',
            },
          },
        }"
        :render-tag="renderTag"
        :render-label="renderLabel"
        :consistent-menu-width="false"
        :to="dropdownTo"
        :placement="placement"
        :disabled="disabled"
        placeholder=""
        @update:value="setValue('')"
      >
        <template #header>
          <NInput
            v-model:value="search"
            size="small"
          >
            <template #prefix>
              <i class="fa-regular fa-magnifying-glass text-icon-hover cursor-pointer" />
            </template>
          </NInput>
        </template>
      </NSelect>
      <NInput
        ref="inputRef"
        v-bind="inputProps"
        :value="value as any"
        @update:value="handleUpdatePhone"
      >
        <template
          v-if="selectedOption"
          #prefix
        >
          +{{ selectedOption.item.dialCode }}
        </template>
      </NInput>
    </NInputGroup>
  </UiFormItem>
</template>
